import { EmptyState, FlexColumn, Loading, MaterialIcon, useModal, useMoment } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { COMMANDS_STATUS, TABLE_COLUMNS_NAME } from '../../constants/commands';
import { useStyles } from '../../hooks/useStyles';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { availableCommandLabelsState } from '../../storage/commands';
import { Command } from '../../types/command';
import { StyledPaginatedTable } from '../shared/StyledPaginatedTable';
import { StyledTooltip } from '../ui/StyledTooltip';
import { useCommands } from './hooks/useCommands';
import CommandDetailModal from './modal/CommandDetailModal';

type Props = {
  commandType?: string;
  status?: string;
  assetId?: string;
  reloadPage?: boolean;
};
export function CommandList({ commandType, status, assetId, reloadPage }: Readonly<Props>) {
  const { css, classes } = useStyles();
  const [selectedCommand, setSelectedCommand] = useState<Command>();
  const [commands, setCommands] = useState<Command[]>();
  const commandLabels = useRecoilValue(availableCommandLabelsState);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { loadCommands, getCommandStatusColor } = useCommands();
  const { formatDateFromTs } = useMoment();
  const { openModal, closeModal, showModal } = useModal();
  const [loading, setLoading] = useState(true);

  useUpdateEffect(() => {
    setPage(0);
  }, [reloadPage]);

  useEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    setLoading(true);
    const loadCommandsTableData = async () => {
      const response = await loadCommands(page, commandType, status, assetId);
      if (response?.error) {
        setCommands(undefined);
        setTotalItems(0);
      } else {
        setCommands(response?.commands);
        setTotalItems(response?.total || 0);
      }
      setLoading(false);
    };
    loadCommandsTableData();
  }, [page, commandType, status, assetId]);

  const onOpenDetailModal = (command: Command) => {
    setSelectedCommand(command);
    openModal();
  };

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
      {!commands && !loading && <EmptyState title="Todavía no envias ningun comando" />}
      {!commands && loading && <Loading />}
      {commands && (
        <>
          {!!commands?.length && (
            <StyledPaginatedTable
              columns={TABLE_COLUMNS_NAME}
              data={commands.map((command) => {
                return {
                  items: [
                    command?.asset.vehicle?.licensePlate || command?.asset.container?.identifier,
                    command.ident,
                    command?.asset.label || '-',
                    commandLabels ? commandLabels[command?.commandType] : '-',
                    formatDateFromTs(+command?.created) || '-',
                    <span
                      key={`status-${command._id}`}
                      className={css({
                        color: getCommandStatusColor(command.status),
                      })}
                    >
                      {COMMANDS_STATUS[command.status]}
                    </span>,
                    <StyledTooltip
                      key={`details-${command._id}`}
                      content={'Ver detalles'}
                      showArrow={false}
                    >
                      <Button
                        size="compact"
                        shape="square"
                        onClick={() => onOpenDetailModal(command)}
                        kind="tertiary"
                      >
                        <MaterialIcon name={'visibility'} />
                      </Button>
                    </StyledTooltip>,
                  ],
                };
              })}
              totalItems={totalItems}
              page={page}
              onPageChange={setPage}
              loading={loading}
            />
          )}
          {!commands.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
          <CommandDetailModal
            onClose={closeModal}
            isOpen={showModal}
            command={selectedCommand}
          />
        </>
      )}
    </FlexColumn>
  );
}
